import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  ChevronLeft, 
  ChevronRight,
  X, 
  Tag, 
  CheckCircle, 
  XCircle, 
  Filter, 
  List,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  SlidersHorizontal,
  RefreshCcw
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from "axios";

const backendUrl = 'https://justurbane.in/backend_php/public';

const AdminCategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'desc' });
  
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [filters, setFilters] = useState({
    global: '',
    displayName: '',
    slug: '',
    subcategory: '',
    status: 'ALL'
  });

  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  const [currentCategory, setCurrentCategory] = useState(null);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '', 
    display_name: '', 
    description: '',
    subcategories: '' 
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchCategories();
  }, [navigate]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      
      const response = await axios.get(`${backendUrl}/api/admin/categories/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (Array.isArray(response.data)) {
        setCategories(response.data);
      }
    } catch (error) {
      toast.error('Failed to load categories');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = "Unique ID/Slug is required";
    if (!formData.display_name.trim()) errors.display_name = "Display Name is required";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const openAddModal = () => {
    setFormData({ name: '', display_name: '', description: '', subcategories: '' });
    setFormErrors({});
    setIsEditing(false);
    setShowModal(true);
  };

  const openEditModal = (category) => {
    let subStr = '';
    if (category.subcategories) {
      try {
        const subArr = typeof category.subcategories === 'string' 
          ? JSON.parse(category.subcategories) 
          : category.subcategories;
        subStr = Array.isArray(subArr) ? subArr.join(', ') : '';
      } catch (e) {
        subStr = '';
      }
    }

    setFormData({
      name: category.name,
      display_name: category.display_name,
      description: category.description || '',
      subcategories: subStr
    });
    setCurrentCategory(category);
    setFormErrors({});
    setIsEditing(true);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    const token = localStorage.getItem('admin_token');

    try {
      const subcategoriesArray = formData.subcategories
        ? formData.subcategories.split(',').map(s => s.trim()).filter(s => s !== '')
        : [];

      const payload = {
        name: formData.name,
        display_name: formData.display_name,
        description: formData.description,
        subcategories: subcategoriesArray
      };

      if (isEditing) {
        await axios.put(
          `${backendUrl}/api/admin/categories/update/${currentCategory.id}`, 
          payload,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        toast.success("Category updated successfully");
      } else {
        await axios.post(
          `${backendUrl}/api/admin/categories/create`, 
          payload,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        toast.success("Category created successfully");
      }

      setShowModal(false);
      fetchCategories();
    } catch (error) {
      console.error(error);
      const msg = error.response?.data?.messages?.name || error.response?.data?.message || "Operation failed";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (category) => {
    const newStatus = category.category_status === "ACTIVE" ? "INACTIVE" : "ACTIVE";

    if (!window.confirm(`Change status to ${newStatus}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem("admin_token");

      if (!token) {
        toast.error("Authentication token missing");
        return;
      }

      await axios.post(
        `${backendUrl}/api/admin/categories/status/${category.id}`,
        {
          category_status: newStatus
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );

      toast.success(`Category status changed to ${newStatus}`);
      fetchCategories();
    } catch (error) {
      console.error(error?.response?.data || error.message);
      toast.error(
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        "Failed to update category status"
      );
    }
  };


  const handleDashboardVisibilityStatusChange = async (category) => {
    const newStatus = category.dashboard_visibility_status === "ACTIVE" ? "INACTIVE" : "ACTIVE";

    if (!window.confirm(`Change status to ${newStatus}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem("admin_token");

      if (!token) {
        toast.error("Authentication token missing");
        return;
      }

      await axios.post(
        `${backendUrl}/api/admin/categories/dashboard_visibility_status/${category.id}`,
        {
          category_status: newStatus
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );

      toast.success(`Category status changed to ${newStatus}`);
      fetchCategories();
    } catch (error) {
      console.error(error?.response?.data || error.message);
      toast.error(
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        "Failed to update category status"
      );
    }
  };


  

  const confirmDelete = (category) => {
    setCategoryToDelete(category);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!categoryToDelete) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(
        `${backendUrl}/api/admin/categories/delete/${categoryToDelete.id}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success("Category deleted");
      setShowDeleteModal(false);
      fetchCategories();
    } catch (error) {
      toast.error("Failed to delete category");
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const processedData = useMemo(() => {
    let data = [...categories];

    if (filters.global) {
      const searchLower = filters.global.toLowerCase();
      data = data.filter(item => 
        item.display_name.toLowerCase().includes(searchLower) ||
        item.name.toLowerCase().includes(searchLower) ||
        (item.description && item.description.toLowerCase().includes(searchLower))
      );
    }

    if (filters.displayName) {
      data = data.filter(item => item.display_name.toLowerCase().includes(filters.displayName.toLowerCase()));
    }
    if (filters.slug) {
      data = data.filter(item => item.name.toLowerCase().includes(filters.slug.toLowerCase()));
    }
    if (filters.status !== 'ALL') {
      data = data.filter(item => item.category_status === filters.status);
    }
    if (filters.subcategory) {
      const subLower = filters.subcategory.toLowerCase();
      data = data.filter(item => {
        let subs = [];
        try {
          subs = typeof item.subcategories === 'string' ? JSON.parse(item.subcategories) : item.subcategories;
        } catch(e) { subs = []; }
        if (!Array.isArray(subs)) return false;
        return subs.some(s => s.toLowerCase().includes(subLower));
      });
    }

    if (sortConfig.key) {
      data.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return data;
  }, [categories, filters, sortConfig]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return processedData.slice(startIndex, startIndex + rowsPerPage);
  }, [processedData, currentPage, rowsPerPage]);

  const totalPages = Math.ceil(processedData.length / rowsPerPage);

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="w-3 h-3 text-gray-400 ml-1" />;
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 text-amber-500 ml-1" />
      : <ArrowDown className="w-3 h-3 text-amber-500 ml-1" />;
  };

  const renderSubcategories = (jsonOrArray) => {
    if (!jsonOrArray) return <span className="text-gray-400 text-xs italic">None</span>;
    
    let items = [];
    try {
      items = typeof jsonOrArray === 'string' ? JSON.parse(jsonOrArray) : jsonOrArray;
    } catch (e) {
      return <span className="text-red-400 text-xs">Error</span>;
    }

    if (!Array.isArray(items) || items.length === 0) return <span className="text-gray-400 text-xs italic">None</span>;

    const display = items.slice(0, 3);
    const remaining = items.length - 3;

    return (
      <div className="flex flex-wrap gap-1">
        {display.map((sub, idx) => (
          <span key={idx} className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-[10px] border border-gray-200">
            {sub}
          </span>
        ))}
        {remaining > 0 && (
          <span className="bg-gray-100 text-gray-500 px-2 py-0.5 rounded text-[10px] border border-gray-200">
            +{remaining}
          </span>
        )}
      </div>
    );
  };

  const resetFilters = () => {
    setFilters({
      global: '',
      displayName: '',
      slug: '',
      subcategory: '',
      status: 'ALL'
    });
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading categories...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
                <span className="hidden sm:inline">Dashboard</span>
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <List className="w-5 h-5 text-amber-500" />
                Category Manager
              </h1>
            </div>
            
            <button
              onClick={openAddModal}
              className="flex items-center space-x-2 bg-amber-500 text-white px-4 py-2 rounded-lg hover:bg-amber-600 transition-colors shadow-sm"
            >
              <Plus className="w-4 h-4" />
              <span>Add Category</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-sm border mb-6">
          <div className="p-4 border-b">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="relative flex-1 max-w-lg">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Global search (Name, ID, Description)..."
                  value={filters.global}
                  onChange={(e) => {
                    setFilters({ ...filters, global: e.target.value });
                    setCurrentPage(1);
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>

              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${showAdvancedSearch ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                >
                  <SlidersHorizontal className="w-4 h-4" />
                  <span>Filters</span>
                </button>
                <button 
                  onClick={resetFilters}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                  title="Reset Filters"
                >
                  <RefreshCcw className="w-4 h-4" />
                </button>
              </div>
            </div>

            {showAdvancedSearch && (
              <div className="mt-4 pt-4 border-t grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 animate-in fade-in slide-in-from-top-2">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Display Name</label>
                  <input 
                    type="text"
                    value={filters.displayName}
                    onChange={(e) => { setFilters({...filters, displayName: e.target.value}); setCurrentPage(1); }}
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-amber-500 outline-none"
                    placeholder="Contains..."
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Slug ID</label>
                  <input 
                    type="text"
                    value={filters.slug}
                    onChange={(e) => { setFilters({...filters, slug: e.target.value}); setCurrentPage(1); }}
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-amber-500 outline-none"
                    placeholder="Contains..."
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Subcategory</label>
                  <input 
                    type="text"
                    value={filters.subcategory}
                    onChange={(e) => { setFilters({...filters, subcategory: e.target.value}); setCurrentPage(1); }}
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-amber-500 outline-none"
                    placeholder="Has tag..."
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                  <select
                    value={filters.status}
                    onChange={(e) => { setFilters({...filters, status: e.target.value}); setCurrentPage(1); }}
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-amber-500 outline-none bg-white"
                  >
                    <option value="ALL">All Status</option>
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('display_name')}
                  >
                    <div className="flex items-center">
                      Display Name {renderSortIcon('display_name')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center">
                      Slug ID {renderSortIcon('name')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subcategories
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('category_status')}
                  >
                    <div className="flex items-center">
                      Status {renderSortIcon('category_status')}
                    </div>
                  </th>

                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('dashboard_visibility_status')}
                  >
                    <div className="flex items-center">
                      Dashboard Status {renderSortIcon('dashboard_visibility_status')}
                    </div>
                  </th>



                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {paginatedData.length > 0 ? (
                  paginatedData.map((cat) => (
                    <tr key={cat.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-sm font-semibold text-gray-900">{cat.display_name}</span>
                          {cat.description && (
                            <p className="text-xs text-gray-400 mt-1 max-w-xs truncate">{cat.description}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-1 rounded">
                          {cat.name}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {renderSubcategories(cat.subcategories)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleStatusChange(cat)}
                          className={`flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                            cat.category_status === 'ACTIVE'
                              ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                              : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200'
                          }`}
                        >
                          {cat.category_status === 'ACTIVE' ? (
                            <CheckCircle className="w-3 h-3" />
                          ) : (
                            <XCircle className="w-3 h-3" />
                          )}
                          <span>{cat.category_status}</span>
                        </button>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleDashboardVisibilityStatusChange(cat)}
                          className={`flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                            cat.dashboard_visibility_status === 'ACTIVE'
                              ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                              : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200'
                          }`}
                        >
                          {cat.dashboard_visibility_status === 'ACTIVE' ? (
                            <CheckCircle className="w-3 h-3" />
                          ) : (
                            <XCircle className="w-3 h-3" />
                          )}
                          <span>{cat.dashboard_visibility_status}</span>
                        </button>
                      </td>


                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => openEditModal(cat)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => confirmDelete(cat)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-10 text-center text-gray-500">
                      <div className="flex flex-col items-center justify-center">
                        <List className="w-10 h-10 text-gray-300 mb-2" />
                        <p>No categories found matching your criteria</p>
                        <button 
                          onClick={resetFilters}
                          className="mt-2 text-amber-600 hover:text-amber-700 text-sm font-medium"
                        >
                          Clear all filters
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="px-6 py-4 border-t bg-gray-50 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Show</span>
              <select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="border rounded px-2 py-1 bg-white focus:ring-2 focus:ring-amber-500 outline-none"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <span>entries</span>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 mr-2">
                Showing {processedData.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1} to {Math.min(currentPage * rowsPerPage, processedData.length)} of {processedData.length} entries
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(curr => Math.max(1, curr - 1))}
                  disabled={currentPage === 1}
                  className="p-2 border rounded-md bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => {
                    return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                  })
                  .map((page, index, array) => (
                    <React.Fragment key={page}>
                      {index > 0 && array[index - 1] !== page - 1 && (
                        <span className="px-2 text-gray-400">...</span>
                      )}
                      <button
                        onClick={() => setCurrentPage(page)}
                        className={`w-8 h-8 flex items-center justify-center rounded-md border text-sm font-medium transition-colors ${
                          currentPage === page
                            ? 'bg-amber-500 text-white border-amber-500'
                            : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                        }`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  ))}
                <button
                  onClick={() => setCurrentPage(curr => Math.min(totalPages, curr + 1))}
                  disabled={currentPage === totalPages || totalPages === 0}
                  className="p-2 border rounded-md bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full transform transition-all">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-900">
                {isEditing ? 'Edit Category' : 'Create New Category'}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-500">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Display Name *</label>
                  <input
                    type="text"
                    value={formData.display_name}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (!isEditing && formData.name === formData.display_name.toLowerCase().replace(/ /g, '-')) {
                         setFormData(prev => ({
                           ...prev,
                           display_name: val,
                           name: val.toLowerCase().replace(/[^a-z0-9]/g, '-')
                         }));
                      } else {
                         setFormData({ ...formData, display_name: val });
                      }
                    }}
                    className={`w-full border rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-amber-500 ${formErrors.display_name ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="e.g. Technology"
                  />
                  {formErrors.display_name && <p className="text-xs text-red-500 mt-1">{formErrors.display_name}</p>}
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unique Slug (ID) *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                    disabled={isEditing} 
                    className={`w-full border rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-amber-500 ${isEditing ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''} ${formErrors.name ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="e.g. technology"
                  />
                  {formErrors.name && <p className="text-xs text-red-500 mt-1">{formErrors.name}</p>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="Optional description..."
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                  <Tag className="w-3 h-3 mr-1" />
                  Subcategories
                </label>
                <input
                  type="text"
                  value={formData.subcategories}
                  onChange={(e) => setFormData({ ...formData, subcategories: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="AI, Blockchain, gadgets (comma separated)"
                />
                <p className="text-xs text-gray-500 mt-1">Separate multiple subcategories with commas</p>
              </div>

              <div className="pt-4 flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-amber-500 text-white px-4 py-2 rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Saving...' : (isEditing ? 'Update Category' : 'Create Category')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteModal && categoryToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full shadow-xl">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">Delete Category</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Are you sure you want to delete "{categoryToDelete.display_name}"? This cannot be undone.
                </p>
              </div>
            </div>
            
            <div className="mt-6 flex space-x-3">
              <button
                onClick={handleDelete}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCategoriesPage;